import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import {
  AlignLeft,
  Calendar,
  CheckSquare,
  ChevronDown,
  Circle,
  Hash,
  Info,
  Mail,
  Trash2,
  Type,
  Upload,
} from 'lucide-react';

interface FieldType {
  id: string;
  labelKey: string;
  fallbackLabel: string;
  icon: React.ElementType;
  descriptionKey: string;
  fallbackDescription: string;
}

const fieldTypes: FieldType[] = [
  { id: 'text', labelKey: 'formBuilder.palette.types.text', fallbackLabel: 'Short text', icon: Type, descriptionKey: 'formBuilder.palette.descriptions.text', fallbackDescription: 'Single line answer' },
  { id: 'email', labelKey: 'formBuilder.palette.types.email', fallbackLabel: 'Email', icon: Mail, descriptionKey: 'formBuilder.palette.descriptions.email', fallbackDescription: 'Email with validation' },
  { id: 'number', labelKey: 'formBuilder.palette.types.number', fallbackLabel: 'Number', icon: Hash, descriptionKey: 'formBuilder.palette.descriptions.number', fallbackDescription: 'Numeric answer' },
  { id: 'textarea', labelKey: 'formBuilder.palette.types.textarea', fallbackLabel: 'Paragraph', icon: AlignLeft, descriptionKey: 'formBuilder.palette.descriptions.textarea', fallbackDescription: 'Long text answer' },
  { id: 'select', labelKey: 'formBuilder.palette.types.select', fallbackLabel: 'Dropdown', icon: ChevronDown, descriptionKey: 'formBuilder.palette.descriptions.select', fallbackDescription: 'Select from a list' },
  { id: 'checkbox', labelKey: 'formBuilder.palette.types.checkbox', fallbackLabel: 'Checkboxes', icon: CheckSquare, descriptionKey: 'formBuilder.palette.descriptions.checkbox', fallbackDescription: 'Multiple choice answers' },
  { id: 'radio', labelKey: 'formBuilder.palette.types.radio', fallbackLabel: 'Multiple choice', icon: Circle, descriptionKey: 'formBuilder.palette.descriptions.radio', fallbackDescription: 'Choose a single option' },
  { id: 'date', labelKey: 'formBuilder.palette.types.date', fallbackLabel: 'Date', icon: Calendar, descriptionKey: 'formBuilder.palette.descriptions.date', fallbackDescription: 'Calendar picker' },
  { id: 'file', labelKey: 'formBuilder.palette.types.file', fallbackLabel: 'File upload', icon: Upload, descriptionKey: 'formBuilder.palette.descriptions.file', fallbackDescription: 'Ask for a file' },
];

const DraggableFieldType: React.FC<{ fieldType: FieldType }> = ({ fieldType }) => {
  const { t } = useTranslation('common');
  const label = t(fieldType.labelKey, { defaultValue: fieldType.fallbackLabel });
  const description = t(fieldType.descriptionKey, { defaultValue: fieldType.fallbackDescription });

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${fieldType.id}`,
    data: fieldType,
  });

  const Icon = fieldType.icon;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`group cursor-grab rounded-2xl border border-slate-200 bg-white p-3 transition active:cursor-grabbing hover:border-brand-300 hover:bg-brand-50/40 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/5 ${isDragging ? 'scale-[0.98] opacity-50' : 'opacity-100'}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 transition group-hover:bg-brand-100 group-hover:text-brand-600 dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-brand-500/10 dark:group-hover:text-brand-300">
          <Icon className="h-4.5 w-4.5" />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-slate-800 dark:text-gray-100">{label}</h4>
          <p className="mt-1 text-[11px] leading-5 text-slate-400 dark:text-gray-500">{description}</p>
        </div>
      </div>
    </div>
  );
};

const FieldPalette: React.FC = () => {
  const { t } = useTranslation('common');
  const tt = (key: string, fallback: string) => t(key, { defaultValue: fallback });

  return (
    <div className="space-y-4 rounded-[12px] border border-slate-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="rounded-[12px] bg-slate-50 p-3 dark:bg-gray-900">
        <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-gray-500">
          {tt('formBuilder.palette.badge', 'Question blocks')}
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
          {tt('formBuilder.palette.description', 'Drag blocks to the form canvas and compose a flow with the Lumina visual style.')}
        </p>
      </div>

      <div className="space-y-2">
        {fieldTypes.map((fieldType) => (
          <DraggableFieldType key={fieldType.id} fieldType={fieldType} />
        ))}
      </div>

      <div className="rounded-[12px] border border-dashed border-slate-300 bg-slate-50 p-4 text-[11px] text-slate-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
        <div className="mb-2 flex items-center gap-2">
          <Info className="h-3.5 w-3.5" />
          <span>{tt('formBuilder.palette.tipSelect', 'Click a card to edit its content')}</span>
        </div>
        <div className="mb-2 flex items-center gap-2">
          <ChevronDown className="h-3.5 w-3.5" />
          <span>{tt('formBuilder.palette.tipReorder', 'Drag cards to reorder the form')}</span>
        </div>
        <div className="flex items-center gap-2">
          <Trash2 className="h-3.5 w-3.5" />
          <span>{tt('formBuilder.palette.tipRemove', 'Use duplicate and delete on each card')}</span>
        </div>
      </div>
    </div>
  );
};

export default FieldPalette;
